import React, { useState, useEffect } from 'react';
import { MessageCircle, User, Bot, Smartphone, MessageSquare } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import { useTranslation } from '../i18n';
import { format } from 'date-fns';

const History = () => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    fetchConversations();
  }, [limit]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/conversations?limit=${limit}`);
      setConversations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load conversations', t));
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'telegram':
      case 'telegram_bot':
        return <MessageSquare size={16} color="#0088cc" />;
      case 'whatsapp':
        return <Smartphone size={16} color="#25d366" />;
      default:
        return <MessageCircle size={16} />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'telegram':
      case 'telegram_bot':
        return 'Telegram';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return source;
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return format(date, 'h:mm a');
      } else if (diffInHours < 168) { // 7 days
        return format(date, 'EEE h:mm a');
      } else {
        return format(date, 'MMM d, h:mm a');
      }
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container" style={{ paddingBottom: '5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="text-lg" style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
            {t('historyTitle')}
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>
            {t('historySubtitle')}
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="limit-select" style={{ fontSize: '0.875rem', color: '#666' }}>
              {t('showEntries')}:
            </label>
            <select 
              id="limit-select" 
              value={limit} 
              onChange={(e) => setLimit(parseInt(e.target.value))}
              style={{
                padding: '0.25rem 0.5rem',
                border: '1px solid #ddd',
                borderRadius: '0.25rem',
                fontSize: '0.875rem'
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="error">{error}</div>
        )}

        {/* Conversations List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {t('recentConversations')} ({conversations.length})
            </h2>
          </div>
          
          {conversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <MessageCircle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>{t('noConversations')}</p>
              <p style={{ fontSize: '0.875rem' }}>
                {t('startChatting')}
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {(Array.isArray(conversations) ? conversations : []).map((conversation) => (
                <div
                  key={conversation.message_id}
                  style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.75rem',
                    backgroundColor: '#fafafa'
                  }}
                >
                  {/* Message Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {getSourceIcon(conversation.source)}
                      <span style={{ fontSize: '0.875rem', color: '#666' }}>
                        {getSourceLabel(conversation.source)}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>
                      {formatTimestamp(conversation.timestamp)}
                    </span>
                  </div>

                  {/* User Message */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <User size={16} color="#666" />
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#333' }}>
                        {t('you')}
                      </span>
                    </div>
                    <div
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        padding: '0.75rem 1rem',
                        borderRadius: '1rem 1rem 0.25rem 1rem',
                        marginLeft: '1.5rem',
                        fontSize: '0.875rem',
                        lineHeight: '1.4'
                      }}
                    >
                      {conversation.message}
                    </div>
                  </div>

                  {/* AI Response */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Bot size={16} color="#28a745" />
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#333' }}>
                        {t('smartgtdAI')}
                      </span>
                    </div>
                    <div
                      style={{
                        backgroundColor: 'white',
                        color: '#333',
                        padding: '0.75rem 1rem',
                        borderRadius: '1rem 1rem 1rem 0.25rem',
                        marginLeft: '1.5rem',
                        border: '1px solid #e0e0e0',
                        fontSize: '0.875rem',
                        lineHeight: '1.4',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {conversation.response}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conversation Stats */}
        {conversations.length > 0 && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{conversations.length}</div>
              <div className="stat-label">{t('totalMessages')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {new Set((Array.isArray(conversations) ? conversations : []).map(c => c.source)).size}
              </div>
              <div className="stat-label">{t('platformsUsed')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {(Array.isArray(conversations) ? conversations : []).filter(c => 
                  new Date(c.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)
                ).length}
              </div>
              <div className="stat-label">{t('last24h')}</div>
            </div>
          </div>
        )}

        {/* Platform Distribution */}
        {conversations.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">{t('platformUsage')}</h2>
            </div>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {Object.entries(
                (Array.isArray(conversations) ? conversations : []).reduce((acc, conv) => {
                  const platform = getSourceLabel(conv.source);
                  acc[platform] = (acc[platform] || 0) + 1;
                  return acc;
                }, {})
              ).map(([platform, count]) => (
                <div
                  key={platform}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {platform === 'Telegram' && <MessageSquare size={16} color="#0088cc" />}
                    {platform === 'WhatsApp' && <Smartphone size={16} color="#25d366" />}
                    <span style={{ fontWeight: '500' }}>{platform}</span>
                  </div>
                  <span className="badge badge-primary">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default History;